import React from 'react';
import Icon from '../../../components/AppIcon';


const QuickAccessDashboard = ({ tickets, knowledgeBase }) => {
  const openTickets = tickets?.filter(t => t?.ticketStatus === 'open')?.length || 0;
  const inProgressTickets = tickets?.filter(t => t?.ticketStatus === 'in_progress')?.length || 0;
  const resolvedTickets = tickets?.filter(t => t?.ticketStatus === 'resolved')?.length || 0;

  const recentTickets = tickets?.slice(0, 3);
  const popularArticles = knowledgeBase?.slice(0, 4);

  const getStatusBadge = (status) => {
    const statusStyles = {
      open: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'Open' },
      in_progress: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'In Progress' },
      pending_client: { bg: 'bg-purple-500/10', text: 'text-purple-600', label: 'Pending Client' },
      resolved: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Resolved' },
      closed: { bg: 'bg-gray-500/10', text: 'text-gray-600', label: 'Closed' }
    };
    return statusStyles?.[status] || statusStyles?.open;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Inbox" size={24} className="text-blue-600" />
            <h3 className="text-sm font-medium text-muted-foreground">Open Tickets</h3>
          </div>
          <p className="text-3xl font-bold text-blue-600">{openTickets}</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="Clock" size={24} className="text-amber-600" />
            <h3 className="text-sm font-medium text-muted-foreground">In Progress</h3>
          </div>
          <p className="text-3xl font-bold text-amber-600">{inProgressTickets}</p>
        </div>
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="CheckCircle" size={24} className="text-green-600" />
            <h3 className="text-sm font-medium text-muted-foreground">Resolved</h3>
          </div>
          <p className="text-3xl font-bold text-green-600">{resolvedTickets}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Tickets */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Clock" size={20} className="text-primary" />
            Recent Tickets
          </h3>
          <div className="space-y-3">
            {recentTickets?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No tickets yet</p>
            ) : (
              recentTickets?.map(ticket => {
                const statusBadge = getStatusBadge(ticket?.ticketStatus);
                return (
                  <div key={ticket?.id} className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-foreground">{ticket?.ticketNumber}</p>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge?.bg} ${statusBadge?.text}`}>
                            {statusBadge?.label}
                          </span>
                        </div>
                        <p className="text-sm text-foreground">{ticket?.subject}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(ticket?.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Popular Knowledge Base Articles */}
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="BookOpen" size={20} className="text-primary" />
            Popular Articles
          </h3>
          <div className="space-y-3">
            {popularArticles?.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No articles available</p>
            ) : (
              popularArticles?.map(article => (
                <div key={article?.id} className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors cursor-pointer">
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 p-2 rounded-md">
                      <Icon name="FileText" size={16} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground mb-1">{article?.title}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Icon name="Eye" size={12} />
                          {article?.viewCount} views
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="ThumbsUp" size={12} />
                          {article?.helpfulCount} helpful
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickAccessDashboard;